// Reusable function for manage chart data
import getHourlyTotalSales from '../dummyDataRes/getHourlyTotalSales.json';
import { MONTHS } from '../../../../components/atoms/const';

interface ChartDataManagement {
  soda: number[] | undefined;
  snack: number[] | undefined;
  label: string[] | undefined;
}

export interface ChartData {
  data: ChartDataManagement,
  total: {
    year: number | undefined;
    sixMonth: number | undefined;
    threeMonth: number | undefined;
    oneMonth: number | undefined;
    week: number | undefined;
    today: number | undefined;
  }
}

interface ChartRes {
  today: typeof getHourlyTotalSales | undefined;
  oneWeek: typeof getHourlyTotalSales | undefined;
  oneMonth: typeof getHourlyTotalSales | undefined;
  threeMonth: typeof getHourlyTotalSales | undefined;
  sixMonth: typeof getHourlyTotalSales | undefined;
  year: typeof getHourlyTotalSales | undefined;
}

const totalCounter = (resData: typeof getHourlyTotalSales): number | undefined => resData.total?.reduce((a, b) => a + b.amount, 0);

const dataManagement = (resData: typeof getHourlyTotalSales, type: 'today' | 'week' | 'oneMonth' | 'threeMonth' | 'sixMonth' | 'year'): ChartDataManagement => {
  const labelResult = () => {
    switch (type) {
      case 'today':
        return resData.total?.map((data, i) => {
          if (i === 24) return '24.00';
          return `${data.dateKey.slice(8, 10)}.00`;
        }).reverse();
      case 'week':
        return resData.total?.map((data) => `${data.dateKey.slice(6, 8)} ${MONTHS[parseInt(data.dateKey.slice(4, 6), 10) - 1]}, ${data.dateKey.slice(8, 10)}.00`).reverse();
      case 'oneMonth':
        return resData.total?.map((data) => `${data.dateKey.slice(6, 8)} ${MONTHS[parseInt(data.dateKey.slice(4, 6), 10) - 1]}`).reverse();
      case 'threeMonth':
        return resData.total?.map((data) => `${data.dateKey.slice(6, 8)} ${MONTHS[parseInt(data.dateKey.slice(4, 6), 10) - 1]}`).reverse();
      case 'sixMonth':
        return resData.total?.map((data) => `Week ${data.dateKey.slice(4, 6)}`).reverse();
      default: // one year
        return resData.total?.map((data) => MONTHS[parseInt(data.dateKey.slice(4, 6), 10) - 1]).reverse();
    }
  };
  return {
    soda: resData.Soda?.map((data) => data.amount).reverse(),
    snack: resData.Snack?.map((data) => data.amount).reverse(),
    label: labelResult(),
  };
};

const handleSales = (chartRes: ChartRes, currentView: number): ChartData | undefined => {
  const {
    today, oneWeek, oneMonth, threeMonth, sixMonth, year,
  } = chartRes;
  if (today && oneWeek && oneMonth && threeMonth && sixMonth && year) {
    const total = {
      year: totalCounter(year),
      sixMonth: totalCounter(sixMonth),
      threeMonth: totalCounter(threeMonth),
      oneMonth: totalCounter(oneMonth),
      week: totalCounter(oneWeek),
      today: totalCounter(today),
    };
    // changing showing data depend on current view for chart
    if (currentView === 0) return { data: dataManagement(today, 'today'), total };
    if (currentView === 1) return { data: dataManagement(oneWeek, 'week'), total };
    if (currentView === 2) return { data: dataManagement(oneMonth, 'oneMonth'), total };
    if (currentView === 3) return { data: dataManagement(threeMonth, 'threeMonth'), total };
    if (currentView === 4) return { data: dataManagement(sixMonth, 'sixMonth'), total };
    if (currentView === 5) return { data: dataManagement(year, 'year'), total };
    return undefined;
  }
  return undefined;
};

const dataExistsChecker = (data: ChartRes | undefined): boolean => {
  if (data) {
    const today = data.today?.total?.map((x) => x.amount) ?? [];
    const week = data.oneWeek?.total?.map((x) => x.amount) ?? [];
    const month = data.oneMonth?.total?.map((x) => x.amount) ?? [];
    const threeMonth = data.threeMonth?.total?.map((x) => x.amount) ?? [];
    const sixMonth = data.sixMonth?.total?.map((x) => x.amount) ?? [];
    const year = data.year?.total?.map((x) => x.amount) ?? [];
    return (!!today.length || !!week.length || !!month.length || !!threeMonth.length || !!sixMonth.length || !!year.length);
  }
  return false;
};

export {
  dataManagement, totalCounter, handleSales, dataExistsChecker,
};
